import React from "react";
import {
  Layout,
  Table,
  Typography,
  Button,
  Modal,
  Input,
  Switch,
  DatePicker,
  Form,
  Space,
  FormInstance,
  InputNumber,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import {
  UserFull,
  UserFullFromJSON,
} from "../../bitmaker/schemas/Users/UserFull";
import { hoursReadable, fromDateToString } from "../../utils";
import { REDMINE_URL } from "../../constants";

const { Content } = Layout;
const { Title } = Typography;
const { Item } = Form;

dayjs.extend(utc);

interface Props {}

interface State {
  users: Array<UserFull>;
  editModalOpen: boolean;
  selectedUser: UserFull | null;
  isNewUser: boolean;
}

export class MembersInformation extends React.PureComponent<Props, State> {
  formRef: React.RefObject<FormInstance>;

  bmUsersColumns: ColumnsType<UserFull> = [
    {
      key: "display_name",
      title: "Display name",
      dataIndex: "display_name",
      align: "center",
      width: "20%",
      render: (_, record) => (
        <span>{record.display_name ? record.display_name : "-"}</span>
      ),
    },
    {
      key: "redmine_login",
      title: "Redmine Login",
      dataIndex: "redmine_login",
      align: "center",
      width: "10%",
      render: (_, record) =>
        record.login ? (
          <a href={`/user/${record.login}`} rel="noreferrer">
            {record.login}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "redmine_id",
      title: "Redmine ID",
      dataIndex: "redmine_id",
      align: "center",
      width: "10%",
      render: (_, record) =>
        record.id ? (
          <a
            href={`${REDMINE_URL}/users/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {record.id}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "github_user",
      title: "Github user",
      dataIndex: "github_user",
      align: "center",
      width: "12%",
      render: (_, record) => (
        <span>{record.github_user ? record.github_user : "-"}</span>
      ),
    },
    {
      key: "slack_id",
      title: "Slack ID",
      dataIndex: "slack_id",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <span>{record.slack_id ? record.slack_id : "-"}</span>
      ),
    },
    {
      key: "start_date",
      title: "Start date",
      dataIndex: "start_date",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <span>
          {record.start_date ? fromDateToString(record.start_date) : "-"}
        </span>
      ),
    },
    {
      key: "fulltime",
      title: "Fulltime",
      dataIndex: "fulltime",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <span>
          {record.fulltime == null ? "-" : record.fulltime ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "availability",
      title: "Availability",
      dataIndex: "availability",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <span>
          {record.availability != null
            ? hoursReadable(record.availability)
            : "-"}
        </span>
      ),
    },
    {
      key: "pto",
      title: "PTO",
      dataIndex: "pto",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <span>{record.pto != null ? record.pto : "-"}</span>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: "8%",
      render: (_, record) => (
        <Button type="primary" onClick={() => this.handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      users: [],
      editModalOpen: false,
      selectedUser: null,
      isNewUser: false,
    };
  }

  public componentDidMount() {
    const handleUsersResponse = (response: any) => {
      this.setState({
        users: (response.data as Array<UserFull>).map(UserFullFromJSON),
      });
    };

    BitmakerAPIClient.get(`/users`, null, handleUsersResponse);
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevState.selectedUser !== this.state.selectedUser &&
      this.state.selectedUser
    ) {
      if (this.formRef && this.formRef.current) {
        const values = {
          ...this.state.selectedUser,
          start_date: this.state.selectedUser.start_date
            ? dayjs(this.state.selectedUser.start_date).utc()
            : undefined,
        };
        this.formRef.current.setFieldsValue(values);
      }
    }

    // Add default values to the form when creating a new user
    if (
      prevState.editModalOpen !== this.state.editModalOpen &&
      this.state.editModalOpen &&
      this.state.isNewUser &&
      this.formRef &&
      this.formRef.current
    ) {
      this.formRef.current.setFieldsValue({
        display_name: "",
        login: "",
        id: "",
        github_user: "",
        slack_id: "",
        start_date: undefined,
        fulltime: false,
        availability: "",
        pto: "",
      });
    }
  }

  private handleEdit = (user: UserFull) => {
    this.setState({
      editModalOpen: true,
      selectedUser: user,
      isNewUser: false,
    });
  };

  private handleAddNewUser = () => {
    this.setState({
      editModalOpen: true,
      selectedUser: {} as UserFull,
      isNewUser: true,
    });
  };

  private handleSave = (values: any) => {
    const { selectedUser, users, isNewUser } = this.state;

    if (isNewUser) {
      const handleCreateResponse = (response: any) => {
        if (response.isAxiosError) {
          alert("Error: " + response.response.data.error);
        } else {
          this.setState({
            users: [...users, UserFullFromJSON(response.data)],
            editModalOpen: false,
            selectedUser: null,
            isNewUser: false,
          });
        }
      };

      const { login, ...payload } = values;
      BitmakerAPIClient.post("/users", payload, handleCreateResponse);
    } else {
      if (!selectedUser) return;

      const handleSaveResponse = (response: any) => {
        if (response.isAxiosError) {
          // Server returned an error response, display a custom error message
          alert("Error: " + response.response.data.error);
        } else {
          // The server did not return any errors, proceed with normal response handling
          const updatedUsers = users.map((user) =>
            user.id === selectedUser?.id
              ? { ...user, ...modifiedFields }
              : user,
          );
          this.setState({
            users: updatedUsers,
            editModalOpen: false,
            selectedUser: null,
          });
        }
      };

      const modifiedFields: Record<string, any> = {};
      Object.keys(values).forEach((key) => {
        if (key === "login") return;

        const editedValue = values[key];
        const selectedValue = selectedUser[key as keyof UserFull];
        const finalValue =
          key === "start_date" && dayjs.isDayjs(editedValue)
            ? editedValue.format("YYYY-MM-DD")
            : editedValue;

        if (key === "start_date") {
          const editedTime = new Date(finalValue).getTime();
          const selectedTime =
            selectedValue instanceof Date
              ? selectedValue.getTime()
              : new Date(selectedValue as any).getTime();

          if (editedTime !== selectedTime) {
            modifiedFields[key] = finalValue;
          }
        } else {
          if (finalValue !== selectedValue) {
            modifiedFields[key] = finalValue;
          }
        }
      });

      if (Object.keys(modifiedFields).length > 0) {
        BitmakerAPIClient.put(
          `/users/${selectedUser.id}`,
          modifiedFields,
          handleSaveResponse,
        );
      } else {
        this.setState({
          editModalOpen: false,
          selectedUser: null,
        });
      }
    }
  };

  private handleFullTimeChange = (checked: boolean) => {
    if (this.formRef.current) {
      this.formRef.current.setFieldsValue({ availability: checked ? 40 : 0 });
    }
  };

  public render() {
    const { users, editModalOpen, selectedUser, isNewUser } = this.state;

    return (
      <Layout>
        <Content>
          <div className="table-section">
            <div className="table-title">
              <Title level={5}>Members Information</Title>
            </div>
            <div className="table-scroll">
              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  onClick={this.handleAddNewUser}
                  style={{ marginBottom: "16px" }}
                >
                  Add New User
                </Button>
              </div>
              <Table
                columns={this.bmUsersColumns}
                dataSource={users}
                size={"large"}
                pagination={false}
                bordered
                rowKey={(record) => record.id}
              />
            </div>
          </div>
        </Content>
        <Modal
          title={isNewUser ? "Add User" : "Edit User"}
          open={editModalOpen}
          onCancel={() =>
            this.setState({ editModalOpen: false, selectedUser: null })
          }
          footer={null}
        >
          <Form onFinish={this.handleSave} ref={this.formRef} className="mt-5">
            {selectedUser && (
              <>
                <Item
                  label="Display name"
                  name="display_name"
                  rules={[
                    { required: true, message: "Please input Display name!" },
                  ]}
                >
                  <Input placeholder="e.g. Firstname Lastname" allowClear />
                </Item>
                <Item label="Redmine login" name="login">
                  <Input
                    placeholder="e.g. firstname.lastname"
                    allowClear
                    disabled
                  />
                </Item>
                <Item
                  label="Redmine ID"
                  name="id"
                  rules={[
                    { required: true, message: "Please input Redmine ID!" },
                  ]}
                >
                  <InputNumber
                    placeholder="e.g. 1"
                    type={"number"}
                    min={1}
                    precision={0}
                  />
                </Item>
                <Item label="Github user" name="github_user">
                  <Input placeholder="e.g. firstname_lastname" allowClear />
                </Item>
                <Item
                  label="Slack ID"
                  name="slack_id"
                  rules={[
                    { required: true, message: "Please input Slack ID!" },
                  ]}
                >
                  <Input placeholder="e.g. U12ABCD3EFG" allowClear />
                </Item>
                <Item
                  label="Start date"
                  name="start_date"
                  rules={[
                    { required: true, message: "Please select Start date!" },
                  ]}
                >
                  <DatePicker
                    format="YYYY-MM-DD"
                    placeholder="Select Start Date"
                  />
                </Item>
                <Item label="Full-time" name="fulltime" valuePropName="checked">
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    onChange={this.handleFullTimeChange}
                  />
                </Item>
                <Item
                  label="Availability"
                  name="availability"
                  rules={[
                    { required: true, message: "Please input Availability!" },
                  ]}
                >
                  <InputNumber
                    placeholder="e.g. 40"
                    type={"number"}
                    min={0}
                    max={40}
                    addonAfter="h"
                    precision={0}
                  />
                </Item>
                <Item
                  label="PTO (Paid Time Off)"
                  name="pto"
                  rules={[{ required: true, message: "Please input PTO!" }]}
                >
                  <InputNumber
                    placeholder="e.g. 8"
                    type={"number"}
                    addonAfter="h"
                  />
                </Item>
              </>
            )}
            <Item style={{ textAlign: "right" }}>
              <Space>
                <Button
                  key="cancel"
                  onClick={() =>
                    this.setState({ editModalOpen: false, selectedUser: null })
                  }
                >
                  Cancel
                </Button>
                <Button key="save" type="primary" htmlType="submit">
                  Save
                </Button>
              </Space>
            </Item>
          </Form>
        </Modal>
      </Layout>
    );
  }
}
